import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState
} from 'react';
import { type FuelThemeMode } from '@deere/fuel-react/useFuelTheme.js';

import { getEnvVar } from '../utils/getEnvVar';

const ENABLED = getEnvVar('VITE_THEME_MODE_SELECTOR_ENABLED') === 'true';

const STORAGE_KEY = 'themeModePreference';
const DEFAULT_PREFERENCE: FuelThemeMode = ENABLED ? 'system' : 'light';

interface ThemeModeContextValue {
  preference: FuelThemeMode;
  setPreference: (preference: FuelThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
  undefined
);

const isValidPreference = (value: string | null): value is FuelThemeMode =>
  value === 'system' || value === 'light' || value === 'dark';

export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [preference, setPreferenceState] = useState<FuelThemeMode>(() => {
    if (typeof window === 'undefined' || !ENABLED) {
      return DEFAULT_PREFERENCE;
    }
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidPreference(stored) ? stored : DEFAULT_PREFERENCE;
  });

  const setPreference = useCallback((next: FuelThemeMode) => {
    setPreferenceState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  return (
    <ThemeModeContext.Provider value={{ preference, setPreference }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};
